import Raven from 'raven-js';
import lodashCompact from 'lodash/fp/compact';
import { hasRoleRenter, hasRolePartner, hasRoleDriver } from 'common/userRoles';

import { anonymousUser } from 'client/constants';
import sendDataToMobileApp, {
  SR_MOBILE_APP_ACTION_TYPE_CURRENT_USER,
} from 'client/helpers/sendDataToMobileApp';
import safeLocalStorage from 'client/lib/safeLocalStorage';
import log from 'client/lib/log';
import getTourList from 'client/actions/Tour/getList';
import getUserBookmarks from 'client/actions/User/getBookmarks';
import getUserCheckAvailabilityListings from 'client/actions/User/getCheckAvailabilityListings';
import getUserViewedListings from 'client/actions/User/getViewedListings';
import getHouseholdApplicationsAction from 'client/actions/Household/getApplications';
import loadRenterProfileAction from 'client/actions/Household/loadRenterProfile';
import loadCurrentPartnerAction from 'client/actions/Partner/loadCurrentPartner';
import loadCurrentPartnerUserAction from 'client/actions/PartnerUser/loadCurrentPartnerUser';
import updateDeviceRegistrationIds from 'client/actions/MobileApp/updateDeviceRegistrationIds';
import getMarketById from 'client/actions/Market/getById';
import partnerAppInstalledApiAction from 'client/actions/User/partnerAppInstalled';
import getDriverByUserIdAction from 'client/actions/Driver/getByUserId';

import * as utilityAuthUserSignup from 'client/actions/UtilityAuth/userSignup';

import { SR_API_ACTION_LOGIN } from 'api/actionTypes';

export async function onLogicSuccess({
  state,
  apiClient,
  utilityApiClient,
  user,
  utilityUser,
  experimentActiveVariants,
}) {
  const mobileAppState = state.get(['mobileApp']);
  const currentUser = { ...anonymousUser, ...user };

  await Promise.all(
    lodashCompact([
      hasRoleRenter(user.roles) && getTourList({ state, apiClient }),
      hasRoleRenter(user.roles) && getUserBookmarks({ state, apiClient }),
      hasRoleRenter(user.roles) && getUserCheckAvailabilityListings({ state, apiClient }),
      hasRoleRenter(user.roles) &&
        getHouseholdApplicationsAction({ state, apiClient }, { userId: user.id }),
      hasRoleRenter(user.roles) && getUserViewedListings({ state, apiClient }),
      hasRoleRenter(user.roles) &&
        loadRenterProfileAction({ state, apiClient }, { userId: user.id }),
      getMarketById({ state, apiClient }, { marketId: user.searchFilters.marketId }),
      hasRolePartner(user.roles) &&
        loadCurrentPartnerAction({ state, apiClient }, { userId: user.id }),
      hasRolePartner(user.roles) &&
        loadCurrentPartnerUserAction({ state, apiClient }, { userId: user.id }),
      mobileAppState.isPartnerApp && partnerAppInstalledApiAction({ state, apiClient }),
      hasRoleDriver(user.roles) &&
        getDriverByUserIdAction({ state, apiClient }, { userId: user.id }),
    ]),
  );

  // Set experiments.
  if (experimentActiveVariants) {
    state.set(['analytics', 'experiments', 'activeVariants'], experimentActiveVariants);
  }

  // Set filters.
  state.set(['search', 'filters'], {
    ...currentUser.searchFilters,
  });

  // Set the new user as the current user in the state.
  state.set('currentUser', currentUser);

  if (utilityUser) {
    if (!utilityApiClient) {
      const error = new Error(
        'UP user detected after SR login, but no utilityApiClient is available.',
      );
      error.name = 'SrUpInvalidUserLoginError';
      log.error(error);
    }
    await utilityAuthUserSignup.onLogicSuccess({
      state,
      utilityApiClient,
      user: utilityUser,
    });
  }

  Raven.setUserContext(currentUser);

  if (mobileAppState.isMobileApp) {
    sendDataToMobileApp({
      actionType: SR_MOBILE_APP_ACTION_TYPE_CURRENT_USER,
      currentUser: currentUser,
    });
  }

  if (
    mobileAppState.isMobileApp &&
    mobileAppState.askedPushNotificationPermission &&
    mobileAppState.deviceRegistrationId
  ) {
    updateDeviceRegistrationIds(
      { state, apiClient },
      { newDeviceRegistrationId: mobileAppState.deviceRegistrationId },
    );
  }
}

export default async function login(
  { state, apiClient, utilityApiClient },
  { phone, password, autoLoginToken },
) {
  const viewedListingIdsStr = safeLocalStorage.getItem('viewedListingIds');
  const viewedListingIds = viewedListingIdsStr ? JSON.parse(viewedListingIdsStr) : [];

  return apiClient.postWithState({
    action: SR_API_ACTION_LOGIN,
    state: state,
    payload: {
      phone,
      password,
      autoLoginToken,
      viewedListingIds,
    },
    onSuccess({ user, utilityUser, experimentActiveVariants }) {
      safeLocalStorage.removeItem('viewedListingIds');
      return onLogicSuccess({
        state,
        apiClient,
        utilityApiClient,
        user,
        utilityUser,
        experimentActiveVariants,
      });
    },
  });
}
